import React from 'react';
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import { Account } from '../../../../domain/models/Account';
import { AccountDummyDetails } from '../hooks/useAccountDetails';
import { useAdminPreferences } from '../../../../application/hooks/useAdminPreferences';

interface Props {
  account: Account;
  details: AccountDummyDetails;
}

interface KpiCardProps {
  label: string;
  value: string | number;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value }) => (
  <Card
    variant="outlined"
    sx={{
      border: '1px solid #E2E8F0',
      borderRadius: '12px',
      boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
    }}
  >
    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          color: '#64748b',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: '#0b1c30' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export const AccountDetailsSummaryTab: React.FC<Props> = ({ account, details }) => {
  const { language } = useAdminPreferences();

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString(language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const mrrFormatted = `${details.currency} ${details.mrr.toLocaleString()}`;
  const lastLogin = formatDate(details.lastLoginAt);
  const renewal = formatDate(details.renewalDate);
  const statusLabel = account.status.charAt(0).toUpperCase() + account.status.slice(1);

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="Status" value={statusLabel} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="Plan" value={details.plan} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="MRR" value={mrrFormatted} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="Renewal Date" value={renewal} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="Last Login" value={lastLogin} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="Monthly Active Users" value={details.mau} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <KpiCard label="Health Score" value={`${details.healthScore} / 100`} />
        </Grid>
      </Grid>
    </Box>
  );
};
